import * as THREE from 'three';

export interface EnemyConfig {
  scene: THREE.Scene;
  position?: THREE.Vector3;
  name?: string;
  level?: number;
  maxHealth?: number;
  color?: number;
}

export type EnemyState = 'idle' | 'patrol' | 'chase' | 'attack' | 'dead' | 'evade';

// PERF: Shared geometries and materials to reduce GPU memory and draw calls
const SharedGeometries = {
  body: new THREE.SphereGeometry(0.6, 6, 4), // Reduced segments: 8,6 -> 6,4
  eye: new THREE.SphereGeometry(0.1, 4, 3),  // Reduced: 6,4 -> 4,3
  pupil: new THREE.SphereGeometry(0.05, 3, 2), // Reduced: 4,4 -> 3,2
  healthBarBg: new THREE.PlaneGeometry(1, 0.1),
  healthBarFill: new THREE.PlaneGeometry(0.98, 0.08),
};

// Pre-scale body geometry once
SharedGeometries.body.scale(1, 0.7, 1);

const SharedMaterials = {
  eye: new THREE.MeshBasicMaterial({ color: 0xffff00 }),
  pupil: new THREE.MeshBasicMaterial({ color: 0x000000 }),
  healthBarBg: new THREE.MeshBasicMaterial({ color: 0x333333, side: THREE.DoubleSide }),
};

export class Enemy {
  public mesh: THREE.Group;
  public id: string;
  public name: string;
  public level: number;

  // Stats
  public health: number;
  public maxHealth: number;
  public damage: number = 10;
  public attackRange: number = 2;
  public aggroRange: number = 8;
  public attackCooldown: number = 1500; // ms

  // Leashing
  private spawnPosition: THREE.Vector3;
  private leashRange: number = 25; // Max distance from spawn before evading
  private evadeSpeed: number = 6; // Speed when returning to spawn

  // State
  private state: EnemyState = 'idle';
  private lastAttackTime: number = 0;
  private target: THREE.Object3D | null = null;

  // Components
  private body: THREE.Mesh;
  private healthBarBg: THREE.Mesh;
  private healthBarFill: THREE.Mesh;

  // Animation
  private floatOffset: number = 0;
  private originalY: number = 0;

  // Death
  private isDead: boolean = false;
  private deathTime: number = 0;

  // PERF: Reusable vectors for movement calculations
  private readonly _moveDir: THREE.Vector3 = new THREE.Vector3();
  private readonly _returnDir: THREE.Vector3 = new THREE.Vector3();

  // PERF: Distance-based update throttling
  private updateSkipCounter: number = 0;
  private lastPlayerDistSq: number = 0;

  // Combat callbacks
  public onAttack?: (damage: number) => void;

  constructor(config: EnemyConfig) {
    this.id = `enemy_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.name = config.name ?? 'Slime';
    this.level = config.level ?? 1;
    this.maxHealth = config.maxHealth ?? 50;
    this.health = this.maxHealth;

    this.mesh = new THREE.Group();

    // PERF: Use shared geometry, only material is unique per enemy (for color)
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: config.color ?? 0x8b0000,
      roughness: 0.3,
      metalness: 0.2,
    });
    this.body = new THREE.Mesh(SharedGeometries.body, bodyMaterial);
    this.body.position.y = 0.4;
    this.body.castShadow = true;
    this.mesh.add(this.body);

    // PERF: Eyes use shared geometry and material
    const leftEye = new THREE.Mesh(SharedGeometries.eye, SharedMaterials.eye);
    leftEye.position.set(-0.2, 0.5, 0.4);
    this.mesh.add(leftEye);

    const leftPupil = new THREE.Mesh(SharedGeometries.pupil, SharedMaterials.pupil);
    leftPupil.position.set(-0.2, 0.5, 0.48);
    this.mesh.add(leftPupil);

    const rightEye = new THREE.Mesh(SharedGeometries.eye, SharedMaterials.eye);
    rightEye.position.set(0.2, 0.5, 0.4);
    this.mesh.add(rightEye);

    const rightPupil = new THREE.Mesh(SharedGeometries.pupil, SharedMaterials.pupil);
    rightPupil.position.set(0.2, 0.5, 0.48);
    this.mesh.add(rightPupil);

    // PERF: Health bar uses shared geometry
    this.healthBarBg = new THREE.Mesh(SharedGeometries.healthBarBg, SharedMaterials.healthBarBg);
    this.healthBarBg.position.y = 1.2;
    this.mesh.add(this.healthBarBg);

    // Health bar fill needs unique material for color changes
    const healthBarFillMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      side: THREE.DoubleSide,
    });
    this.healthBarFill = new THREE.Mesh(SharedGeometries.healthBarFill, healthBarFillMaterial);
    this.healthBarFill.position.y = 1.2;
    this.healthBarFill.position.z = 0.01;
    this.mesh.add(this.healthBarFill);

    // Set position
    if (config.position) {
      this.mesh.position.copy(config.position);
    }
    this.originalY = this.mesh.position.y;

    // Store spawn position for leashing
    this.spawnPosition = this.mesh.position.clone();

    // Add to scene
    config.scene.add(this.mesh);
  }

  public update(deltaTime: number, playerPosition?: THREE.Vector3): void {
    if (this.isDead) {
      this.updateDeath(deltaTime);
      return;
    }

    // PERF: Distance-based update throttling and LOD
    // Far enemies update less frequently to save CPU
    if (playerPosition) {
      const dx = this.mesh.position.x - playerPosition.x;
      const dz = this.mesh.position.z - playerPosition.z;
      this.lastPlayerDistSq = dx * dx + dz * dz;

      // PERF: Simple LOD - hide details at distance
      // Eyes and health bar hidden at medium distance, reduce update rate at far
      const showDetails = this.lastPlayerDistSq < 225; // < 15 units
      const showHealth = this.lastPlayerDistSq < 625; // < 25 units

      // Toggle visibility of detail meshes (eyes are children 1-4, health bar 5-6)
      for (let i = 1; i < 5; i++) {
        if (this.mesh.children[i]) {
          this.mesh.children[i].visible = showDetails;
        }
      }
      this.healthBarBg.visible = showHealth && !this.isDead;
      this.healthBarFill.visible = showHealth && !this.isDead;

      // Skip updates based on distance (enemies far away update less often)
      if (this.lastPlayerDistSq > 900) { // > 30 units
        this.updateSkipCounter++;
        if (this.updateSkipCounter < 4) return; // Update every 4th frame
        this.updateSkipCounter = 0;
      } else if (this.lastPlayerDistSq > 400) { // > 20 units
        this.updateSkipCounter++;
        if (this.updateSkipCounter < 2) return; // Update every 2nd frame
        this.updateSkipCounter = 0;
      }
    }

    // Idle floating animation
    this.floatOffset += deltaTime * 2;
    this.body.position.y = 0.4 + Math.sin(this.floatOffset) * 0.1;

    // Make health bar face camera (billboard effect done in render)
    this.updateHealthBar();

    // AI behavior
    if (playerPosition) {
      this.updateAI(deltaTime, playerPosition);
    }
  }

  private updateAI(deltaTime: number, playerPosition: THREE.Vector3): void {
    const distanceToPlayer = this.mesh.position.distanceTo(playerPosition);
    const distanceFromSpawn = this.mesh.position.distanceTo(this.spawnPosition);

    switch (this.state) {
      case 'idle':
        if (distanceToPlayer < this.aggroRange) {
          this.state = 'chase';
        }
        break;

      case 'chase':
        // Check leash range - if too far from spawn, evade
        if (distanceFromSpawn > this.leashRange) {
          this.state = 'evade';
          break;
        }

        if (distanceToPlayer > this.aggroRange * 1.5) {
          // Lost aggro
          this.state = 'evade';
        } else if (distanceToPlayer <= this.attackRange) {
          this.state = 'attack';
        } else {
          // Move towards player - reuse vector
          const direction = this._moveDir
            .subVectors(playerPosition, this.mesh.position)
            .normalize();
          direction.y = 0; // Keep on ground

          this.mesh.position.x += direction.x * 2 * deltaTime;
          this.mesh.position.z += direction.z * 2 * deltaTime;

          // Face player
          this.mesh.lookAt(playerPosition.x, this.mesh.position.y, playerPosition.z);
        }
        break;

      case 'attack':
        // Check leash range while attacking too
        if (distanceFromSpawn > this.leashRange) {
          this.state = 'evade';
          break;
        }

        if (distanceToPlayer > this.attackRange * 1.2) {
          this.state = 'chase';
        } else {
          // Face player
          this.mesh.lookAt(playerPosition.x, this.mesh.position.y, playerPosition.z);

          // Attack if cooldown ready
          const now = Date.now();
          if (now - this.lastAttackTime >= this.attackCooldown) {
            this.lastAttackTime = now;
            // Attack animation (squash and stretch)
            this.body.scale.set(1.3, 0.5, 1.3);

            // Trigger attack callback for damage
            const attackDamage = this.damage + Math.floor(Math.random() * 5);
            this.onAttack?.(attackDamage);

            setTimeout(() => {
              if (!this.isDead) {
                this.body.scale.set(1, 1, 1);
              }
            }, 200);
          }
        }
        break;

      case 'evade':
        // Return to spawn point quickly, regenerating health
        const distToSpawn = this.mesh.position.distanceTo(this.spawnPosition);

        if (distToSpawn < 0.5) {
          // Reached spawn, reset and become idle
          this.state = 'idle';
          this.health = this.maxHealth; // Full heal on evade
          this.target = null;
        } else {
          // Move quickly back to spawn - reuse vector
          const returnDir = this._returnDir
            .subVectors(this.spawnPosition, this.mesh.position)
            .normalize();
          returnDir.y = 0;

          this.mesh.position.x += returnDir.x * this.evadeSpeed * deltaTime;
          this.mesh.position.z += returnDir.z * this.evadeSpeed * deltaTime;
          this.mesh.lookAt(this.spawnPosition.x, this.mesh.position.y, this.spawnPosition.z);

          // Regenerate health while evading
          this.health = Math.min(this.maxHealth, this.health + this.maxHealth * 0.2 * deltaTime);
        }
        break;
    }
  }

  public isEvading(): boolean {
    return this.state === 'evade';
  }

  private updateHealthBar(): void {
    const healthPercent = this.health / this.maxHealth;
    this.healthBarFill.scale.x = Math.max(0, healthPercent);
    this.healthBarFill.position.x = (1 - healthPercent) * -0.49;

    // Color based on health
    const material = this.healthBarFill.material as THREE.MeshBasicMaterial;
    if (healthPercent > 0.5) {
      material.color.setHex(0x00ff00);
    } else if (healthPercent > 0.25) {
      material.color.setHex(0xffff00);
    } else {
      material.color.setHex(0xff0000);
    }
  }

  public takeDamage(amount: number): boolean {
    if (this.isDead) return false;

    this.health -= amount;

    // Flash red
    const bodyMat = this.body.material as THREE.MeshStandardMaterial;
    const originalColor = bodyMat.color.getHex();
    bodyMat.color.setHex(0xffffff);
    setTimeout(() => {
      if (!this.isDead) {
        bodyMat.color.setHex(originalColor);
      }
    }, 100);

    // Knockback effect
    this.body.scale.set(1.2, 0.8, 1.2);
    setTimeout(() => {
      if (!this.isDead) {
        this.body.scale.set(1, 1, 1);
      }
    }, 150);

    if (this.health <= 0) {
      this.die();
      return true; // Enemy died
    }

    return false;
  }

  private die(): void {
    this.isDead = true;
    this.deathTime = 0;
    this.state = 'dead';

    // Hide health bar
    this.healthBarBg.visible = false;
    this.healthBarFill.visible = false;

    console.log(`💀 ${this.name} died!`);
  }

  private updateDeath(deltaTime: number): void {
    this.deathTime += deltaTime;

    // Shrink and fade
    const scale = Math.max(0, 1 - this.deathTime * 2);
    this.body.scale.set(scale, scale * 0.5, scale);

    // Sink into ground
    this.mesh.position.y = this.originalY - this.deathTime * 0.5;

    // Fade out
    const bodyMat = this.body.material as THREE.MeshStandardMaterial;
    bodyMat.opacity = Math.max(0, 1 - this.deathTime * 2);
    bodyMat.transparent = true;
  }

  public isDeadAndRemovable(): boolean {
    return this.isDead && this.deathTime > 1;
  }

  public getState(): EnemyState {
    return this.state;
  }

  public isAlive(): boolean {
    return !this.isDead;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public destroy(): void {
    this.mesh.parent?.remove(this.mesh);
  }
}
